import { useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Heading,
  Spinner,
  Alert,
  AlertIcon,
  Badge,
  Card,
  CardBody,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useNudgeTemplatesQuery, nudgeKeys } from "../hooks";
import { NudgeTemplate } from "../types";
import apiClient from "services/ApiClient";

export default function AvailableTemplates({ onTemplateAdded }: { onTemplateAdded: () => void }) {
  const { data: templates, isLoading, error } = useNudgeTemplatesQuery();
  const [selectedTemplate, setSelectedTemplate] = useState<NudgeTemplate | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const toast = useToast();
  const queryClient = useQueryClient();

  const handleAddTemplate = async (template: NudgeTemplate) => {
    setIsAdding(true);
    try {
      await apiClient.post("/api/nudge-preferences", {
        nudgePreference: {
          nudgeTemplateId: template.id,
          enabled: true,
          settings: {},
        },
      });

      // Invalidate preferences query to refresh the list
      queryClient.invalidateQueries({ queryKey: nudgeKeys.preferences.all });

      toast({
        title: "Template added",
        description: `${template.name} has been added to your preferences`,
        status: "success",
        duration: 3000,
      });
      setSelectedTemplate(null);
      onTemplateAdded();
    } catch (error: any) {
      toast({
        title: "Error adding template",
        description: error.message || "Failed to add template to preferences",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsAdding(false);
    }
  };

  if (isLoading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="lg" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        Error loading templates
      </Alert>
    );
  }

  const availableTemplates = templates?.filter(t => t.active);

  const typeLabels: Record<string, string> = {
    weekly_milestone_digest: "Weekly Digests",
    milestone_reminder: "Milestone Reminders",
    day_of_prompt: "Day-of Prompts",
    task_overdue: "Task Overdue Alerts",
    journey_stuck: "Journey Stuck Notifications",
  };

  return (
    <>
      <VStack spacing={4} align="stretch">
        <Heading size="md">Available Nudge Templates</Heading>
        <Text color="gray.600">
          Browse and add nudge templates to customize your notification preferences.
        </Text>

        {availableTemplates?.length === 0 && (
          <Alert status="info">
            <AlertIcon />
            No templates available at this time.
          </Alert>
        )}

        <VStack spacing={3} align="stretch">
          {availableTemplates?.map((template) => (
            <Card key={template.id} variant="outline">
              <CardBody>
                <HStack justify="space-between">
                  <VStack align="start" spacing={2} flex={1}>
                    <HStack>
                      <Text fontWeight="bold">{template.name}</Text>
                      {template.systemNudge && (
                        <Badge colorScheme="purple" size="sm">System</Badge>
                      )}
                    </HStack>
                    <Badge colorScheme="blue" size="sm">
                      {typeLabels[template.nudgeType] || template.nudgeType}
                    </Badge>
                    {template.description && (
                      <Text fontSize="sm" color="gray.600">
                        {template.description}
                      </Text>
                    )}
                  </VStack>
                  <VStack spacing={2}>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      onClick={() => setSelectedTemplate(template)}
                    >
                      View Details
                    </Button>
                    <Button
                      size="sm"
                      colorScheme="green"
                      onClick={() => handleAddTemplate(template)}
                      isLoading={isAdding}
                    >
                      Add to Preferences
                    </Button>
                  </VStack>
                </HStack>
              </CardBody>
            </Card>
          ))}
        </VStack>
      </VStack>

      {/* Template Details Modal */}
      <Modal isOpen={!!selectedTemplate} onClose={() => setSelectedTemplate(null)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{selectedTemplate?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="start" spacing={3}>
              <HStack>
                <Badge colorScheme="blue" size="sm">
                  {selectedTemplate && (typeLabels[selectedTemplate.nudgeType] || selectedTemplate.nudgeType)}
                </Badge>
                {selectedTemplate?.systemNudge && (
                  <Badge colorScheme="purple" size="sm">System Template</Badge>
                )}
              </HStack>

              {selectedTemplate?.description && (
                <Box>
                  <Text fontWeight="semibold">Description:</Text>
                  <Text>{selectedTemplate.description}</Text>
                </Box>
              )}

              {selectedTemplate?.rules && Object.keys(selectedTemplate.rules).length > 0 && (
                <Box>
                  <Text fontWeight="semibold">Configuration:</Text>
                  <Box pl={4} mt={2}>
                    <pre style={{ fontSize: "12px", overflow: "auto" }}>
                      {JSON.stringify(selectedTemplate.rules, null, 2)}
                    </pre>
                  </Box>
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setSelectedTemplate(null)}>
              Close
            </Button>
            <Button
              colorScheme="green"
              onClick={() => selectedTemplate && handleAddTemplate(selectedTemplate)}
              isLoading={isAdding}
            >
              Add to Preferences
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
